import {
  useEffect,
  useState,
} from 'react';
import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  PatientCountSummaryDTO,
  StickyNotificationDTO,
} from 'dtos';
import {
  getApiPatientGetCountsSummaryPatientId,
} from 'endpoints';
import {
  appointmentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useSignalREffect from 'views/EMR/hooks/signalR';

type PatientCountSummaryOptions = {
  enabled?: boolean;
};

type AppointmentsRefreshProps = {
  enabled?: boolean;
  onCreate?: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
};

function useGetPatientCountSummary(
  patientId: number,
  {
    enabled = true,
  }: PatientCountSummaryOptions = {},
): UseQueryResult<PatientCountSummaryDTO, Error> {
  return useQuery<PatientCountSummaryDTO, Error>({
    queryKey: ['patient-count-summary', patientId],
    queryFn: ({ signal }) => getApiPatientGetCountsSummaryPatientId(patientId, { signal }),
    enabled: enabled && patientId > 0,
    staleTime: 0,
  });
}

export function useAppointmentsRefresh({
  enabled = true,
  onCreate,
  onUpdate,
  onDelete,
}: AppointmentsRefreshProps) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const unsubscribeFromCreate = appointmentsEventChannel.on('onEventCreated', (created) => {
      if (created) {
        onCreate?.();
      }
    });
    const unsubscribeFromUpdate = appointmentsEventChannel.on('onEventUpdated', () => {
      onUpdate?.();
    });
    const unsubscribeFromDelete = appointmentsEventChannel.on('onEventDeleted', () => {
      onDelete?.();
    });

    return () => {
      unsubscribeFromCreate();
      unsubscribeFromUpdate();
      unsubscribeFromDelete();
    };
  }, [enabled, onCreate, onDelete, onUpdate]);
}

export default function usePatientSummaryCount(
  patientId: number,
  {
    enabled = true,
  }: PatientCountSummaryOptions = {},
) {
  const { data, refetch } = useGetPatientCountSummary(patientId, { enabled });
  const [stickyCount, setStickyCount] = useState<number | undefined>(data?.stickyCount);
  const [toDoCount, setToDoCount] = useState<number | undefined>(data?.toDoCount);
  const [upcomingAppointmentsCount, setUpcomingAppointmentsCount] = useState<number | undefined>(data?.upcomingAppointmentsCount);

  useEffect(() => {
    setStickyCount(data?.stickyCount ?? 0);
    setToDoCount(data?.toDoCount ?? 0);
    setUpcomingAppointmentsCount(data?.upcomingAppointmentsCount ?? 0);
  }, [data]);

  useSignalREffect('OnStickyUpdated', ({
    patientId: signaledPatientId,
    stickyCount,
  }: StickyNotificationDTO) => {
    if (signaledPatientId === patientId) {
      setStickyCount(stickyCount ?? 0);
    }
  }, [patientId]);

  useSignalREffect('OnToDoUpdated', (_todoId: number, signaledPatientId: number) => {
    if (enabled && signaledPatientId === patientId) {
      refetch();
    }
  }, [enabled, patientId, refetch]);

  useAppointmentsRefresh({
    enabled: enabled && patientId > 0,
    onCreate: refetch,
    onDelete: refetch,
  });

  return { stickyCount, toDoCount, upcomingAppointmentsCount };
}
